import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, timeout, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Interface pentru request-ul de semnare
 */
export interface OperatorSignRequest {
    pdfBase64: string;
    isOperator: boolean;
    signatureX: number;
    signatureY: number;
    signatureWidth: number;
    signatureHeight: number;
    signaturePage: number;
    signatureImageBase64?: string;
    signatureText?: string;
}

/**
 * Interface pentru răspunsul de semnare
 */
export interface OperatorSignResponse {
    signedPdfBase64: string;
    timestamp: string;
    success: boolean;
    message?: string;
}

/**
 * Interface pentru log-ul de semnare
 */
export interface OperatorSignLogData {
    user_id: number;
    user_name: string;
    directie_id?: number;
    directie_name?: string;
    compartiment_id?: number;
    compartiment_name?: string;
    document_generat_id: number;
    document_registratura_id: number;
    document_validare_id?: number | null;
    doc_exemplar_type: 'operator' | 'cetatean';
    has_user_signature?: boolean;
    status?: 'initiated' | 'success' | 'failed';
    error_message?: string;
    error_code?: number;
    request_payload?: any;
    response_data?: any;
}

/**
 * Serviciu pentru semnătura electronică a operatorului
 * Comunică cu utilitarul local pe localhost:5000
 */
@Injectable({
    providedIn: 'root'
})
export class OperatorSignService {
    // URL-ul serviciului local de semnare (rulează pe PC-ul operatorului)
    private readonly signServiceUrl = 'http://localhost:5000';

    // URL-ul backend-ului Laravel pentru logging
    private readonly basePath = environment.interop.basePath;

    constructor(private http: HttpClient) {}

    /**
     * Verifică dacă serviciul local de semnare rulează
     * @returns Observable care emite true dacă serviciul e online
     */
    checkServiceHealth(): Observable<boolean> {
        return this.http.get(`${this.signServiceUrl}/api/health`).pipe(
            timeout(5000),
            map(() => true),
            catchError(() => of(false))
        );
    }

    /**
     * Trimite PDF-ul pentru semnare la serviciul local
     * @param request Datele pentru semnare
     * @returns Observable cu răspunsul conținând PDF-ul semnat
     */
    signPdf(request: OperatorSignRequest): Observable<OperatorSignResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this.http.post<OperatorSignResponse>(
            `${this.signServiceUrl}/api/sign-pdf`,
            request,
            { headers }
        ).pipe(
            timeout(30000), // 30 secunde timeout
            catchError(this.handleSigningError)
        );
    }

    /**
     * Semnează PDF cu semnătura utilizatorului din profil (dacă există)
     * @param request Datele pentru semnare
     * @returns Promise cu răspunsul conținând PDF-ul semnat
     */
    async signPdfWithUserSignature(request: OperatorSignRequest): Promise<OperatorSignResponse> {
        try {
            // Încearcă să obțină semnătura utilizatorului
            const userSignature = await this.getUserSignature().toPromise();

            if (userSignature) {
                request.signatureImageBase64 = userSignature;
            }

            const response = await this.signPdf(request).toPromise();
            if (!response) {
                throw new Error('Nu s-a primit răspuns de la serviciul de semnare');
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obține semnătura operatorului din profil (dacă există)
     * @returns Observable cu semnătura în format base64 sau null
     */
    getUserSignature(): Observable<string | null> {
        return this.http.get<any>(`${this.basePath}api/registratura/user/signature`).pipe(
            map(response => {
                // Semnătura vine în response.data.content ca base64 data URI
                // Format: "data:image/png;base64,..."
                if (response?.data?.content) {
                    const content = response.data.content;
                    // Dacă e deja un data URI, extragem doar partea base64
                    if (content.startsWith('data:')) {
                        const base64Part = content.split(',')[1];
                        return base64Part || null;
                    }
                    return content;
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    /**
     * Inițiază un log pentru operațiunea de semnare
     * @param logData Datele pentru log
     * @returns Observable cu ID-ul log-ului creat
     */
    initiateLog(logData: OperatorSignLogData): Observable<number> {
        const payload = {
            ...logData,
            status: 'initiated'
        };

        return this.http.post<any>(
            `${this.basePath}api/registratura/document/generabile/operator-signature/log`,
            payload
        ).pipe(
            map(response => response?.data?.id || 0),
            catchError(error => {
                console.error('Eroare la crearea log-ului:', error);
                return of(0);
            })
        );
    }

    /**
     * Actualizează log-ul cu rezultatul operațiunii
     * @param logId ID-ul log-ului
     * @param updateData Datele de actualizare
     * @returns Observable
     */
    updateLog(logId: number, updateData: Partial<OperatorSignLogData>): Observable<any> {
        if (!logId) {
            return of(null);
        }

        return this.http.put<any>(
            `${this.basePath}api/registratura/document/generabile/operator-signature/log/${logId}`,
            updateData
        ).pipe(
            catchError(error => {
                console.error('Eroare la actualizarea log-ului:', error);
                return of(null);
            })
        );
    }

    /**
     * Obține statisticile de semnare
     * @returns Observable cu statisticile
     */
    getSigningStats(): Observable<any> {
        return this.http.get<any>(
            `${this.basePath}api/registratura/document/generabile/operator-signature/stats`
        ).pipe(
            map(response => response?.data || {}),
            catchError(() => of({}))
        );
    }

    /**
     * Obține statusul semnării pentru un document specific
     * @param documentGeneratId ID-ul documentului generat
     * @returns Observable cu statusul semnării
     */
    getDocumentSigningStatus(documentGeneratId: number): Observable<any> {
        return this.http.get<any>(
            `${this.basePath}api/registratura/document/generabile/operator-signature/log/status/${documentGeneratId}`
        ).pipe(
            map(response => response?.data || null),
            catchError(error => {
                console.error('Eroare la obținerea statusului semnării:', error);
                return of(null);
            })
        );
    }

    /**
     * Obține logurile de semnare pentru un document
     * @param documentId ID-ul documentului
     * @returns Observable cu lista de loguri
     */
    getLogsByDocument(documentId: number): Observable<any[]> {
        return this.http.get<any>(
            `${this.basePath}api/registratura/document/generabile/operator-signature/log/document/${documentId}`
        ).pipe(
            map(response => response?.data || []),
            catchError(error => {
                console.error('Eroare la obținerea logurilor:', error);
                return of([]);
            })
        );
    }

    /**
     * Generează PDF din conținutul HTML al documentului
     * @param documentGeneratId ID-ul documentului generat
     * @param type Tipul documentului: 'operator' sau 'cetatean'
     * @returns Observable cu Blob-ul PDF-ului
     */
    generatePdfFromContent(documentGeneratId: number, type: 'operator' | 'cetatean'): Observable<Blob> {
        return this.http.get(
            `${this.basePath}api/registratura/document/generabile/operator-signature/generate-pdf-from-content/${documentGeneratId}/${type}`,
            { responseType: 'blob' }
        ).pipe(
            catchError(error => {
                console.error('Eroare la generarea PDF-ului:', error);
                return throwError(() => ({
                    message: 'Nu s-a putut genera PDF-ul din conținut',
                    status: error.status || 0,
                    originalError: error
                }));
            })
        );
    }

    /**
     * Convertește un File în base64
     * @param file Fișierul de convertit
     * @returns Promise cu string-ul base64
     */
    fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                // Elimină prefixul "data:application/pdf;base64,"
                const base64 = (reader.result as string).split(',')[1];
                resolve(base64);
            };

            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    }

    /**
     * Convertește un Blob în base64
     * @param blob Blob-ul de convertit
     * @returns Promise cu string-ul base64
     */
    blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                resolve(base64);
            };

            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Convertește base64 înapoi în Blob pentru download/upload
     * @param base64 String-ul base64
     * @param contentType Tipul de conținut (default: application/pdf)
     * @returns Blob-ul rezultat
     */
    base64ToBlob(base64: string, contentType: string = 'application/pdf'): Blob {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    }

    /**
     * Crează URL pentru preview PDF
     * @param base64 String-ul base64 al PDF-ului
     * @returns URL-ul pentru preview
     */
    createPdfPreviewUrl(base64: string): string {
        const blob = this.base64ToBlob(base64);
        return URL.createObjectURL(blob);
    }

    /**
     * Handler pentru erori de semnare
     */
    private handleSigningError(error: any): Observable<never> {
        let errorMessage = 'A apărut o eroare necunoscută la semnare';

        if (error.error instanceof ErrorEvent) {
            // Eroare client-side
            errorMessage = `Eroare: ${error.error.message}`;
        } else {
            // Eroare server-side sau de conexiune
            if (error.status === 0) {
                errorMessage = 'Nu se poate conecta la serviciul local de semnare. Asigurați-vă că SignTool rulează pe localhost:5000';
            } else if (error.name === 'TimeoutError') {
                errorMessage = 'Timpul de răspuns a expirat. Operațiunea de semnare durează prea mult.';
            } else {
                errorMessage = `Eroare server: ${error.status} - ${error.message || error.statusText}`;
            }
        }

        console.error('Eroare semnare electronică:', errorMessage, error);
        return throwError(() => ({
            message: errorMessage,
            status: error.status || 0,
            originalError: error
        }));
    }
}
